import { useEffect, useRef, useLayoutEffect } from 'react'
import { Link } from "react-router-dom";
import { Box, Typography, Stack, Button } from '@mui/material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'

import pc_background_image from '@assets/images/main/main_businessintro_background_pc.png'
import mo_background_image from '@assets/images/main/main_businessintro_background_mo.png'

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useIsMobile } from '@/hooks/Responsive'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'

gsap.registerPlugin(ScrollTrigger)

// 접근성: prefers-reduced-motion
function usePrefersReducedMotion() {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export default function BusinessIntroSection() {
  const { isMobile } = useIsMobile()
  const lang = useLang()

  const fixedRef = useRef(null)
  const containerRef = useRef(null)
  const videoRef = useRef(null)
  const imageRef = useRef(null)
  const buttonRef = useRef(null)
  const textRef = useRef(null)
  const stackRef = useRef(null)

  useEffect(() => {
    if (!fixedRef.current) return;

    // 스크롤바 폭 계산
    const getScrollbarWidth = () =>
      window.innerWidth - document.documentElement.clientWidth;
      const w = getScrollbarWidth();

    // padding-right 적용 함수
    const applyPadding = () => {
      const isLocked =
        document.body.style.overflow === "hidden" ||
        document.body.classList.contains("MuiModal-open");

      if (isLocked) {
        if (document.documentElement.style.scrollbarGutter === 'stable') {
          fixedRef.current.style.paddingRight = "0px";
        } else {
          fixedRef.current.style.paddingRight = `${w}px`;
        }
      } else {
        fixedRef.current.style.paddingRight = "0px";
      }
    };

    // 최초 1회 실행
    applyPadding();

    // body 변화 감지
    const observer = new MutationObserver(applyPadding);

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    return () => {
      observer.disconnect();
      if (fixedRef.current) fixedRef.current.style.paddingRight = "0px";
    };
  }, [lang]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          pin: true,
          onEnter: () => {
            containerRef.current?.setAttribute('aria-hidden', 'false')
          },
          onLeave: () => {
            containerRef.current?.setAttribute('aria-hidden', 'true')
          },
          scrub: 1,
          start: 'top top',
          end: '+=200%',
        },
      })

      tl.fromTo(videoRef.current, { y: 10 }, { y: 0, duration: 0.1 }, 'key1')
        .fromTo(textRef.current, { opacity: 0 }, { opacity: 1, duration: 0.1 }, 'key2')
        .fromTo(
          videoRef.current,
          { clipPath: isMobile ? 'inset(50% 4% 26% round 16px)' : 'inset(60% 4% 0% round 16px)' },
          { clipPath: 'inset(20% 15% 20% round 16px)' },
          'key2'
        )
        .to(buttonRef.current, { opacity: 0, y: 100 }, 'key2')
        .fromTo(
          imageRef.current,
          { scale: 1.1, y: isMobile ? 100 : 500 },
          { scale: 1, y: 0 },
          'key2'
        )
        .fromTo(
          videoRef.current,
          { clipPath: 'inset(20% 15% 20% round 20px)' },
          { clipPath: 'inset(0% 0% 0% round 0px)' },
          'key3'
        )
        .to(fixedRef.current, { opacity: 0, duration: 0.2 }, '>-0.1')
    })

    setTimeout(() => ScrollTrigger.refresh(), 0)

    const handleResize = () => {
      ScrollTrigger.refresh()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      ctx.revert()
    }
  }, [lang, isMobile])

  useLayoutEffect(() => {
    if (isMobile) return

    const ctx = gsap.context(() => {
      gsap.from(stackRef.current, {
        scrollTrigger: {
          trigger: stackRef.current,
          start: 'top 65%',
          end: 'top 40%',
          scrub: 0.2,
        },
        opacity: 0,
        y: 60,
        scale: 0.98,
        duration: 1,
        ease: 'power2.out',
      })
    })

    setTimeout(() => ScrollTrigger.refresh(), 0)

    return () => ctx.revert()
  }, [lang, isMobile])

  return (
    <>
      <Box
        ref={fixedRef}
        aria-hidden="true"
        sx={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          width: '100vw',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          zIndex: -1,
        }}
      >
        <Box sx={{ px: 2 }}>
          {lang === 'ko' && (
            <Typography component="p" variant={isMobile ? 'body1' : 'subtitle3'} fontWeight={700} sx={{ color: 'primary.main', mb: 2 }}>
              The Core Hub for Immersive Education
            </Typography>
          )}
          <Typography component="h2" fontWeight={isMobile ? 700 : 800} variant={isMobile ? 'h3' : 'h1'} sx={{ lineHeight: isMobile ? 1.3 : 1.5, mb: 4 }}>
            실감형 교육의 Core Hub
          </Typography>
          <Typography component="p" variant={isMobile ? 'body2' : 'h6'} fontWeight={500} sx={{ opacity: 0 }}>
            기관과 기술, 학습자와 데이터를 유기적으로{isMobile ? <br /> : <span>&nbsp;</span>}연결하는 공유 플랫폼,<br />
            이제 실감형 교육의 중심 허브가 됩니다.
          </Typography>
        </Box>
      </Box>
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          mx: 'auto',
          minHeight: '100vh',
          overflow: 'hidden',
          zIndex: 2,
        }}
      >
        <Box
          ref={videoRef}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            zIndex: 3,
          }}
        >
        <Box
          ref={textRef}
          sx={{
            position: 'absolute',
            left: '50%',
            top: isMobile ? 'calc(50% + 11px)' : '50%',
            width: '100vw',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'text.contrastText',
            px: 2,
            zIndex: 3,
          }}
        >
          {lang === 'ko' && (
            <Typography component="p" variant={isMobile ? 'body1' : 'subtitle3'} fontWeight={700} sx={{ color: 'primary.main', mb: 2 }}>
              The Core Hub for Immersive Education
            </Typography>
          )}
          <Typography component="h2" fontWeight={isMobile ? 700 : 800} variant={isMobile ? 'h3' : 'h1'} sx={{ lineHeight: isMobile ? 1.3 : 1.5, mb: 4 }}>
            실감형 교육의 Core Hub
          </Typography>
          <Typography component="p" variant={isMobile ? 'subtitle2' : 'h6'} fontWeight={500}>
            기관과 기술, 학습자와 데이터를 유기적으로{isMobile ? <br /> : <span>&nbsp;</span>}연결하는 공유 플랫폼,<br />
            이제 실감형 교육의 중심 허브가 됩니다.
          </Typography>
        </Box>
        <Box
          component="img"
          ref={imageRef}
          src={isMobile ? mo_background_image : pc_background_image}
          alt="VR체험 이미지"
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        </Box>
        <Box
          ref={buttonRef}
          sx={{
            position: 'absolute',
            left: '50%',
            top: isMobile ? '52%' : '61%',
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center',
            width: isMobile ? '64px' : '72px', 
            height: isMobile ? '64px' : '72px',
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
          }}
        >
          <ArrowDownwardIcon
            role="img"
            aria-label="아래로 스크롤"
            sx={{
              fontSize: isMobile ? '24px' : '32px',
              color: 'text.contrastText',
              animation: 'bounce 2s infinite',
            }}
          />
        </Box>
      </Box>
      <Stack 
        ref={stackRef}
        alignItems="center" 
        justifyContent="center" 
        sx={{ 
          textAlign: 'center', 
          bgcolor: 'background.paper', 
          height: isMobile ? 'auto' : '695px',
          px: 2,
          py: 10
        }}
      >
        <Typography component="p" variant={isMobile ? 'subtitle2' : 'subtitle3'} fontWeight={700} sx={{ color: 'primary.main', mb: 2 }}>
          OpenVLab은?
        </Typography>
        <Typography component="p" variant={isMobile ? 'h6' : 'h4'} fontWeight={600} sx={{ mb: 5 }}>
          한국방송통신대학교가 교육부의 'AID 30+ 프로젝트' 일환으로 <br />
          구축하는 개방형 공공 학습 플랫폼입니다.
        </Typography>
        <Typography component="p" sx={{ lineHeight: 1.7, mb: 7 }}>
          대학, 기업, 개발자들이 만든 XR(VR/AR) 학습 콘텐츠를 공유하고, 고등·평생·직업교육기관 학습자에게 무료로 제공해 교육 접근성을 높입니다. <br />
          또한, 고비용·고위험 실험을 대체하는 가상 실험실습실 서비스를 제공하여 교육 예산의 효율성을 향상시키는 것을 목표로 합니다.
        </Typography>
        <Button
          size="large"
          variant="outlined"
          color="primary"
          component={Link}
          to={buildLangHref('/about/intro', lang)}
        >
          자세히 보기
        </Button>
      </Stack>
    </>
  )
}
